import { createSelector } from 'reselect';
import {
  iDeviceDetails,
  iPerson,
  iList,
  iTag,
  iFullStoreState,
} from '../../shared/interfaces';
import { createKeyMatchSelector } from './general-selectors';
import { toLower, test, pathOr, fromPairs } from 'ramda';

const ADVANCED_SEARCH_PREFIX = '__advanced_search__:';

type AdvancedSearchCondition = {
  id: string;
  fieldId: string;
  fieldLabel: string;
  operator: 'startsWith' | 'contains' | 'greaterThan' | 'lessThan' | 'equals';
  value: string;
};

const getTerm = (state: iFullStoreState) => state.general.searchTerm;
const getLowerTerm = createSelector(getTerm, (term) => term.toLowerCase());
const devicesDetails = (state: iFullStoreState) =>
  state.devicesData.devicesDetails;
const allPeople = (state: iFullStoreState) => state.general.people || {};
const allTags = (state: iFullStoreState) =>
  state.general.tags || ({} as iList<iTag>);

const smush = (...groups) => {
  let ret = {};

  groups.forEach((g) => Object.keys(g).forEach((id) => (ret[id] = g[id])));

  return ret;
};

const parseAdvancedSearch = (term: string): AdvancedSearchCondition[] => {
  if (!term || !term.startsWith(ADVANCED_SEARCH_PREFIX)) return [];

  try {
    const payload = term.slice(ADVANCED_SEARCH_PREFIX.length);
    const parsed = JSON.parse(payload);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const extractComparableValue = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && typeof value.text === 'string') return value.text;
  return value?.toString?.() || '';
};

const findExtraInfoValueByLabelId = (itemExtraInfo: iList<any>, labelId: string): string => {
  const direct = itemExtraInfo?.[labelId];
  if (direct !== undefined) return extractComparableValue(direct);

  const nestedCategoryValue = Object.values(itemExtraInfo || {}).find((entry: any) => {
    return !!entry && typeof entry === 'object' && entry.isCategory && entry[labelId] !== undefined;
  }) as any;

  if (nestedCategoryValue?.[labelId] !== undefined) {
    return extractComparableValue(nestedCategoryValue[labelId]);
  }

  return '';
};

const normalizeNumeric = (value: string): number => {
  const cleaned = (value || '').replace(/[$,\s]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const compareByOperator = (sourceValue: string, operator: AdvancedSearchCondition['operator'], queryValue: string): boolean => {
  const source = (sourceValue || '').toLowerCase();
  const query = (queryValue || '').toLowerCase();

  if (operator === 'startsWith') return source.startsWith(query);
  if (operator === 'contains') return source.includes(query);
  if (operator === 'equals') return source === query;

  const sourceNumber = normalizeNumeric(sourceValue);
  const queryNumber = normalizeNumeric(queryValue);

  if (Number.isNaN(sourceNumber) || Number.isNaN(queryNumber)) return false;
  if (operator === 'greaterThan') return sourceNumber > queryNumber;
  if (operator === 'lessThan') return sourceNumber < queryNumber;

  return false;
};

const matchesAdvancedConditions = (itemExtraInfo: iList<any>, conditions: AdvancedSearchCondition[]): boolean => {
  if (!conditions?.length) return false;

  return conditions.every((condition) => {
    const source = findExtraInfoValueByLabelId(itemExtraInfo, condition.fieldId);
    return compareByOperator(source, condition.operator, condition.value);
  });
};

const matchesSearch = (obj, term, path) => {
  const getObjProp = pathOr('', path);

  const res = Object.entries(obj).filter(([key, value]) => {
    const propValue = getObjProp(value);
    const getLoweredProp = toLower(propValue);
    return getLoweredProp.includes(term);
  });

  return fromPairs(res);
};

// device group
export const devicesMatchSelector = (function () {
  const deviceNameMatchSelector = createSelector(
    getLowerTerm,
    devicesDetails,
    (term, devicesDetails) =>
      matchesSearch(devicesDetails.toObject(), term, ['name'])
  );

  const smushDevices = createSelector(
    deviceNameMatchSelector,
    (...devices) => smush(...devices) as iList<iDeviceDetails>
  );

  return createKeyMatchSelector(smushDevices, (devices) => devices);
})();

export const extraInfoDevicesMatchSelector = (function () {
  const deviceExtraInfoMatchSelector = createSelector(
    getTerm,
    devicesDetails,
    (term, devicesDetails) => {
      if (!term) return {} as iList<iDeviceDetails>;

      const advancedConditions = parseAdvancedSearch(term);

      if (advancedConditions.length) {
        return devicesDetails.reduce((prev, value, key) => {
          const extraInfo = value['extra-info'] ?? {};
          if (matchesAdvancedConditions(extraInfo, advancedConditions)) {
            prev[key] = value;
          }
          return prev;
        }, {} as iList<iDeviceDetails>);
      }

      const terms = term.toLowerCase().split(/\s+/).filter(Boolean);

      const res = devicesDetails.reduce((prev, value, key) => {

        const extraValues = Object.values(value['extra-info'] ?? {}).map((v) => {
          if (typeof v === 'string') return v.toLowerCase();
          if (typeof v === 'object' && v !== null && typeof (v as any).text === 'string') return (v as any).text.toLowerCase();
          return v?.toString?.().toLowerCase() || '';
        });

        const isMatched = terms.every(t =>
          extraValues.some(ev =>
            ev.split(/\s+/).some(word => word.includes(t))
          )
        );

        if (isMatched) prev[key] = value;

        return prev;
      }, {} as iList<iDeviceDetails>);

      return res;
    }
  );

  return createKeyMatchSelector(deviceExtraInfoMatchSelector, (devices) => devices);
})();


export const extraInfoPeopleMatchSelector = (function () {
  const deviceNameMatchSelector = createSelector(
    getTerm,
    allPeople,
    (term, allPeople) => {
      if (!term) return {} as iList<iPerson>;

      const advancedConditions = parseAdvancedSearch(term);

      if (advancedConditions.length) {
        return Object.entries(allPeople).reduce((prev, [key, value]) => {
          const extraInfo = value['extra-info'] ?? {};
          if (matchesAdvancedConditions(extraInfo, advancedConditions)) {
            prev[key] = value;
          }
          return prev;
        }, {} as iList<iPerson>);
      }

      const loweredTerm = term.toLowerCase();
      const res = Object.entries(allPeople).reduce((prev, [key, value]) => {
        const isMached = Object.values(value['extra-info'] ?? {}).some((v) => {
          let searchValue = '';
          if (typeof v === 'string') {
            searchValue = v;
          } else if (typeof v === 'object' && v !== null && typeof (v as any).text === 'string') {
            searchValue = (v as any).text;
          } else {
            searchValue = v?.toString?.() || '';
          }
          return toLower(searchValue).includes(loweredTerm);
        });
        if (isMached) {
          prev[key] = value;
        }
        return prev;
      }, {} as iList<iPerson>);
      return res;
    }
  );

  return createKeyMatchSelector(deviceNameMatchSelector, (people) => people);
})();

export const peopleMatchSelector = (function () {
  const personNameMatchSelector = createSelector(
    getLowerTerm,
    allPeople,
    (term, people) => matchesSearch(people, term, ['displayName'])
  );

  const smushPeople = createSelector(
    personNameMatchSelector,
    (...people) => smush(...people) as iList<iPerson>
  );

  return createKeyMatchSelector(smushPeople, (people) => people);
})();

export const tagsMatchSelector = (function () {
  const tagNameMatcheSelector = createSelector(
    getLowerTerm,
    allTags,
    (term, tags) => matchesSearch(tags, term, ['details', 'name'])
  );

  const smushTags = createSelector(
    tagNameMatcheSelector,
    (...tags) => smush(...tags) as iList<iTag>
  );

  return createKeyMatchSelector(smushTags, (tags) => tags);
})();
